import React, { useState, useEffect, Fragment } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Row, Col, Card, Table, Badge, Button, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import Swal from "sweetalert2";
import SignatureModal from "../Common/SignatureModal";

const GSTReconciliation = () => {
 const navigate = useNavigate();
 const [loading, setLoading] = useState(true);
 const [data, setData] = useState({ summary: {}, transactions: [] });
 const [projects, setProjects] = useState([]);
 const [offices, setOffices] = useState([]);
 const [pdfLoading, setPdfLoading] = useState(false);
 const [excelLoading, setExcelLoading] = useState(false);
 
 // State for SignatureModal
 const [showSignModal, setShowSignModal] = useState(false);
 const [selectedCWO, setSelectedCWO] = useState(null);
 const [statusToUpdate, setStatusToUpdate] = useState("");

 const [filters, setFilters] = usePersistentFilters("gst_recon_filters", {
   fromDate: "",
   toDate: "",
   officeId: "",
   projectId: ""
 });

 useEffect(() => {
   fetchProjects();
   fetchOffices();
 }, []);

 useEffect(() => {
   fetchData();
 }, [filters.fromDate, filters.toDate, filters.officeId, filters.projectId]);

 const fetchProjects = async () => {
   try {
     const res = await api.get("/projects");
     if (res.data.success) {
       setProjects(res.data.data);
     }
   } catch (err) {
     console.error("Error fetching projects:", err);
   }
 };

 const fetchOffices = async () => {
   try {
     const res = await api.get("/offices");
     if (res.data.success) {
       setOffices(res.data.data || []);
     }
   } catch (err) {
     console.error("Error fetching offices:", err);
   }
 };

 const confirmStatusUpdate = (cwo, status, file) => {
   setShowSignModal(false);
 };

 const fetchData = async () => {
   try {
     setLoading(true);
     let url = "/reconciliation/gst-summary?";
     if (filters.fromDate) url += `fromDate=${filters.fromDate}&`;
     if (filters.toDate) url += `toDate=${filters.toDate}&`;
     if (filters.officeId) url += `officeId=${filters.officeId}&`;
     if (filters.projectId) url += `projectId=${filters.projectId}&`;
     
     const res = await api.get(url);
     if (res.data.success) {
       setData(res.data.data);
     }
   } catch (err) {
     console.error("Error fetching GST data:", err);
   } finally {
     setLoading(false);
   }
 };

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-IN', {
 style: 'currency',
 currency: 'INR',
 maximumFractionDigits: 0
 }).format(amount || 0);
 };

 const handleDownloadPdf = async () => {
   try {
     setPdfLoading(true);
     const token = localStorage.getItem("token");
     const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
     const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
     
     let url = `${cleanBaseUrl}/api/reconciliation/gst-pdf?`;
     if (filters.fromDate) url += `fromDate=${filters.fromDate}&`;
     if (filters.toDate) url += `toDate=${filters.toDate}&`;
     if (filters.officeId) url += `officeId=${filters.officeId}&`;
     if (filters.projectId) url += `projectId=${filters.projectId}&`;

     const response = await fetch(url, {
       method: 'GET',
       headers: {
         'Authorization': `Bearer ${token}`,
         'Accept': 'application/pdf'
       }
     });

     if (!response.ok) {
       const errorData = await response.json().catch(() => ({}));
       throw new Error(errorData.message || "Failed to generate PDF");
     }

     const blob = await response.blob();
     const downloadUrl = window.URL.createObjectURL(blob);
     const link = document.createElement('a');
     link.href = downloadUrl;
     link.setAttribute('download', `GST_Reconciliation_${new Date().getTime()}.pdf`);
     document.body.appendChild(link);
     link.click();
     link.remove();
     window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 3000);
   } catch (err) {
     console.error(err);
     Swal.fire("Error", "Failed to generate PDF", "error");
   } finally {
     setPdfLoading(false);
   }
 };

 const handlePreviewPdf = async () => {
   try {
     setPdfLoading(true);
     const token = localStorage.getItem("token");
     const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
     const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
     
     let url = `${cleanBaseUrl}/api/reconciliation/gst-pdf?`;
     if (filters.fromDate) url += `fromDate=${filters.fromDate}&`;
     if (filters.toDate) url += `toDate=${filters.toDate}&`;
     if (filters.officeId) url += `officeId=${filters.officeId}&`;
     if (filters.projectId) url += `projectId=${filters.projectId}&`;

     const response = await fetch(url, {
       method: 'GET',
       headers: {
         'Authorization': `Bearer ${token}`,
         'Accept': 'application/pdf'
       }
     });

     if (!response.ok) throw new Error("Failed to generate PDF");

     const blob = await response.blob();
     const fileURL = window.URL.createObjectURL(
       new Blob([blob], { type: "application/pdf" })
     );

     window.open(fileURL, "_blank");
   } catch (err) {
     console.error(err);
     Swal.fire("Error", "Failed to preview PDF", "error");
   } finally {
     setPdfLoading(false);
   }
 };

 const handleDownloadExcel = async () => {
   try {
     setExcelLoading(true);
     const token = localStorage.getItem("token");
     const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
     const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
     
     let url = `${cleanBaseUrl}/api/reconciliation/gst-excel?`;
     if (filters.fromDate) url += `fromDate=${filters.fromDate}&`;
     if (filters.toDate) url += `toDate=${filters.toDate}&`;
     if (filters.officeId) url += `officeId=${filters.officeId}&`;
     if (filters.projectId) url += `projectId=${filters.projectId}&`;

     const response = await fetch(url, {
       method: 'GET',
       headers: {
         'Authorization': `Bearer ${token}`,
         'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
       }
     });

     if (!response.ok) throw new Error("Failed to generate Excel");

     const blob = await response.blob();
     const downloadUrl = window.URL.createObjectURL(blob);
     const link = document.createElement('a');
     link.href = downloadUrl;
     link.setAttribute('download', `GST_Reconciliation_${new Date().getTime()}.xlsx`);
     document.body.appendChild(link);
     link.click();
     link.remove();
     window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 3000);
   } catch (err) {
     console.error(err);
     Swal.fire("Error", "Failed to download Excel", "error");
   } finally {
     setExcelLoading(false);
   }
 };

 const filteredProjects = filters.officeId
   ? projects.filter(p => {
       const oId = p.office?._id || p.office;
       return oId && String(oId) === String(filters.officeId);
     })
   : projects;

 return (
   <Fragment>
     <PageTitle activeMenu="GST Reconciliation" motherMenu="Accounts" />
     
     <Row className="mb-4">
       <Col lg={12}>
         <Card className="border-0 shadow-sm overflow-hidden">
           <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
             <div>
               <h4 className="mb-0 text-dark fw-bold">GST Reconciliation</h4>
               <p className="mb-0 text-muted small">Comprehensive tax liability report across all projects and offices</p>
             </div>
             <div className="d-flex gap-2">
               <Button variant="light" size="sm" onClick={() => navigate(-1)} className="border shadow-none">
                 <i className="fa fa-arrow-left me-2"></i> Back
               </Button>
               <Button variant="outline-success" size="sm" onClick={handleDownloadExcel} disabled={excelLoading}>
                 {excelLoading ? <Spinner size="sm" className="me-2" /> : <i className="fa fa-file-excel me-2"></i>}
                 Export Excel
               </Button>
               <Button variant="outline-primary" size="sm" onClick={handlePreviewPdf} disabled={pdfLoading}>
                 <i className="fa fa-eye me-2"></i> Preview PDF
               </Button>
               <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={pdfLoading}>
                 {pdfLoading ? <Spinner size="sm" className="me-2" /> : <i className="fa fa-file-pdf me-2"></i>}
                 Download PDF
               </Button>
             </div>
           </Card.Header>
           <Card.Body className="bg-light border-bottom py-3">
             <Row className="g-3 align-items-end">
               <Col md={3}>
                 <Form.Group>
                   <Form.Label className="small fw-bold text-muted text-uppercase">Office</Form.Label>
                   <Form.Select 
                     size="sm" 
                     className="border-0 shadow-none"
                     value={filters.officeId}
                     onChange={(e) => setFilters({ ...filters, officeId: e.target.value, projectId: "" })}
                   >
                     <option value="">All Offices</option>
                     {offices.map(o => {
                        const locationParts = [
                          (o.officeName && o.officeName !== o.companyName) ? o.officeName : null,
                          o.location,
                          o.state,
                          (!o.state && !o.location && o.address) ? o.address : null
                        ].filter(Boolean);
                        const label = locationParts.length > 0 
                          ? `${o.companyName || o.officeName || "Office"} (${locationParts.join(", ")})`
                          : (o.companyName || o.officeName || o.name || "Office");
                        return (
                          <option key={o._id} value={o._id}>
                            {label}
                          </option>
                        );
                      })}
                   </Form.Select>
                 </Form.Group>
               </Col>
               <Col md={3}>
                 <Form.Group>
                   <Form.Label className="small fw-bold text-muted text-uppercase">Project</Form.Label>
                   <Form.Select 
                     size="sm" 
                     className="border-0 shadow-none"
                     value={filters.projectId}
                     onChange={(e) => setFilters({ ...filters, projectId: e.target.value })}
                   >
                     <option value="">All Projects</option>
                     {filteredProjects.map(p => (
                       <option key={p._id} value={p._id}>
                         {p.projectCode ? `${p.projectCode} - ${p.projectName}` : p.projectName}
                       </option>
                     ))}
                   </Form.Select>
                 </Form.Group>
               </Col>
               <Col md={2}>
                 <Form.Group>
                   <Form.Label className="small fw-bold text-muted text-uppercase">From Date</Form.Label>
                   <Form.Control 
                     type="date" 
                     size="sm" 
                     className="border-0 shadow-none"
                     value={filters.fromDate}
                     onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                   />
                 </Form.Group>
               </Col>
               <Col md={2}>
                 <Form.Group>
                   <Form.Label className="small fw-bold text-muted text-uppercase">To Date</Form.Label>
                   <Form.Control 
                     type="date" 
                     size="sm" 
                     className="border-0 shadow-none"
                     value={filters.toDate}
                     onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                   />
                 </Form.Group>
               </Col>
               <Col md={2} className="text-end">
                 <Button variant="outline-danger" size="sm" className="w-100 border-0 shadow-none" onClick={() => setFilters({ fromDate: "", toDate: "", officeId: "", projectId: "" })}>
                   <i className="fa fa-undo me-2"></i> Reset
                 </Button>
               </Col>
             </Row>
           </Card.Body>
 </Card>
 </Col>
 </Row>

 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary spinner-border-sm mb-3"></div>
 <p className="text-muted small">Calculating GST metrics...</p>
 </div>
 ) : (
 <>
 <Row className="mb-4 g-4">
 <Col md={4}>
 <Card className="border-0 shadow-sm text-center p-4 bg-white h-100 border-top border-success border-4">
 <div className="mb-3">
 <div className="bg-success-light rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
 <i className="fa fa-arrow-down text-success"></i>
 </div>
 </div>
 <h6 className="text-muted text-uppercase small fw-bold mb-2">GST Received</h6>
 <h3 className="text-dark fw-bold mb-0">{formatCurrency(data.summary.totalGSTReceived)}</h3>
 <p className="text-muted small mt-2 mb-0">Total Output Tax Collected</p>
 </Card>
 </Col>
 <Col md={4}>
 <Card className="border-0 shadow-sm text-center p-4 bg-white h-100 border-top border-danger border-4">
 <div className="mb-3">
 <div className="bg-danger-light rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
 <i className="fa fa-arrow-up text-danger"></i>
 </div>
 </div>
 <h6 className="text-muted text-uppercase small fw-bold mb-2">GST Paid (ITC)</h6>
 <h3 className="text-dark fw-bold mb-0">{formatCurrency(data.summary.totalGSTPaid)}</h3>
 <p className="text-muted small mt-2 mb-0">Input Tax Credit on Procurement</p>
 </Card>
 </Col>
 <Col md={4}>
 <Card className="border-0 shadow-sm text-center p-4 bg-dark text-white h-100 border-top border-primary border-4">
 <div className="mb-3">
 <div className="bg-primary-light rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
 <i className="fa fa-calculator text-primary"></i>
 </div>
 </div>
 <h6 className="text-white-50 text-uppercase small fw-bold mb-2">Net GST Payable</h6>
 <h3 className="text-white fw-bold mb-0">{formatCurrency(data.summary.remainingGST)}</h3>
 <p className="text-white-50 small mt-2 mb-0">Tax Liability to Government</p>
 </Card>
 </Col>
 </Row>

 <Row>
 <Col lg={12}>
 <Card className="border-0 shadow-sm">
 <Card.Header className="bg-white border-bottom py-3">
 <h5 className="mb-0 fw-bold">Transaction History</h5>
 </Card.Header>
 <Card.Body className="p-0">
 <Table hover responsive className="mb-0">
 <thead className="bg-light">
 <tr>
 <th className="ps-4 py-3 text-uppercase small fw-bold text-muted">Date</th>
 <th className="py-3 text-uppercase small fw-bold text-muted">Reference No</th>
 <th className="py-3 text-uppercase small fw-bold text-muted">Type</th>
 <th className="py-3 text-uppercase small fw-bold text-muted">Project</th>
 <th className="py-3 text-uppercase small fw-bold text-muted">Vendor / Client</th>
 <th className="py-3 text-uppercase small fw-bold text-muted text-end">CGST</th>
 <th className="py-3 text-uppercase small fw-bold text-muted text-end">SGST</th>
 <th className="py-3 text-uppercase small fw-bold text-muted text-end">IGST</th>
 <th className="py-3 text-uppercase small fw-bold text-muted text-end pe-4">Total GST</th>
 </tr>
 </thead>
 <tbody>
 {data.transactions.map((item, index) => (
 <tr key={index} className="align-middle">
 <td className="ps-4">
 <span className="text-dark fw-medium">
 {new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
 </span>
 </td>
 <td className="fw-bold text-primary">{item.no}</td>
 <td>
 <Badge bg="light" className="text-dark border font-w400">{item.type}</Badge>
 </td>
 <td className="text-muted small">{item.project}</td>
 <td className="text-muted small">{item.vendor || item.source}</td>
 <td className="text-end small">{formatCurrency(item.cgst)}</td>
 <td className="text-end small">{formatCurrency(item.sgst)}</td>
 <td className="text-end small">{formatCurrency(item.igst)}</td>
 <td className={`text-end pe-4 fw-bold ${item.direction === 'Received' ? 'text-success' : 'text-danger'}`}>
 {item.direction === 'Received' ? '+' : '-'} {formatCurrency(item.gstAmount)}
 </td>
 </tr>
 ))}
 {data.transactions.length === 0 && (
 <tr>
 <td colSpan="9" className="text-center py-5 text-muted">
 <i className="fa fa-folder-open-o fa-3x mb-3 d-block opacity-25"></i>
 No transactions found for the selected period.
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </>
 )}
 <SignatureModal
 show={showSignModal}
 onHide={() => setShowSignModal(false)}
 onSave={(file) => confirmStatusUpdate(selectedCWO, statusToUpdate, file)}
 title="Authorize Approval"
 />

 {/* PDF Loader Overlay */}
 {pdfLoading && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing GST Reconciliation Report…</h6>
 </div>
 </div>
 )}
 </Fragment>
 );
};

export default GSTReconciliation;
